import React, { useRef } from 'react';
import { Box, Grid, Avatar, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { BUNDLED_AVATARS } from '../static/avatars';
import { useAvatarUpload } from '../hooks/useAvatarUpload';
import CartoonButton from './CartoonButton.component';
import theme from '../theme';

interface AvatarPickerProps {
  value: { type: 'bundled' | 'custom'; avatar: string } | null;
  onChange: (type: 'bundled' | 'custom', avatar: string) => void;
  takenAvatars?: string[];
}

const selectedSx = {
  background: theme.palette.primary.main,
  boxShadow: '0 2px 0 #212121, 0 4px 6px rgba(0,0,0,0.2)',
  transform: 'translateY(3px)',
} as const;

const normalSx = {
  background: 'rgba(255,255,255,0.88)',
  boxShadow: '0 5px 0 #212121, 0 10px 10px rgba(0,0,0,0.25)',
} as const;

const AvatarPicker: React.FC<AvatarPickerProps> = ({ value, onChange, takenAvatars = [] }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadedAvatar, error, handleFileSelect, isUploading } = useAvatarUpload();

  React.useEffect(() => {
    if (uploadedAvatar) onChange('custom', uploadedAvatar);
  }, [uploadedAvatar, onChange]);

  const isCustomSelected = value?.type === 'custom';

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom sx={{ color: 'white', fontWeight: 'bold' }}>
        Choose your avatar
      </Typography>
      <Grid container spacing={1}>
        {/* Upload button */}
        <Grid item xs={3}>
          <CartoonButton
            onClick={() => fileInputRef.current?.click()}
            variant="contained"
            sx={{
              width: '100%', minWidth: 0, height: '56px',
              display: 'flex', flexDirection: 'column', gap: '2px',
              fontSize: '0.55rem', py: 0.5, px: 0, textShadow: 'none',
              ...(isCustomSelected ? selectedSx : normalSx),
            }}
          >
            {isCustomSelected ? (
              <Avatar src={value!.avatar} sx={{ width: 28, height: 28 }} />
            ) : (
              <PhotoCameraIcon sx={{ fontSize: '1.4rem', color: '#333' }} />
            )}
            <span style={{ color: isCustomSelected ? 'white' : '#333' }}>Upload</span>
          </CartoonButton>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
          />
        </Grid>

        {/* Emoji buttons */}
        {BUNDLED_AVATARS.map(av => {
          const isTaken = takenAvatars.includes(av.emoji);
          const isSelected = value?.avatar === av.emoji && value?.type === 'bundled';
          return (
            <Grid item xs={3} key={av.id}>
              <CartoonButton
                onClick={() => !isTaken && onChange('bundled', av.emoji)}
                title={isTaken ? 'Already taken' : av.label}
                variant="contained"
                sx={{
                  width: '100%', minWidth: 0, height: '56px',
                  fontSize: '1.8rem', lineHeight: 1, py: 0, px: 0,
                  textShadow: 'none', position: 'relative',
                  cursor: isTaken ? 'not-allowed' : 'pointer',
                  opacity: isTaken ? 0.38 : 1,
                  ...(isSelected ? selectedSx : normalSx),
                  ...(!isTaken && !isSelected && {
                    '&:hover': {
                      filter: 'brightness(1.05)',
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 0 #212121, 0 12px 20px rgba(0,0,0,0.3)',
                    },
                  }),
                }}
              >
                {av.emoji}
                {isTaken && (
                  <Box sx={{ position: 'absolute', top: 2, right: 5, fontSize: '0.55rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1 }}>✕</Box>
                )}
              </CartoonButton>
            </Grid>
          );
        })}
      </Grid>

      {error && (
        <Typography color="error" variant="caption">{error}</Typography>
      )}
      {isUploading && (
        <Typography variant="caption" color="white">Uploading...</Typography>
      )}
    </Box>
  );
};

export default AvatarPicker;
